/**
 * Ficha de modelo (deep-link por `model_ref`) — Sprint 286 Stream E;
 * REQ-MINT-006 §2 / anexo §2.2 (LLD §4).
 *
 * Cada bloque se pinta con su procedencia/fecha; los huecos y los datos
 * `stale` se muestran como tales. La ficha **no cambia configuración de
 * ejecución**: solo informa y enlaza.
 */

import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  MessageSquare,
  Newspaper,
  Radar as RadarIcon,
} from "lucide-react";
import {
  CartesianGrid,
  Line,
  LineChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button, Card, CardContent, Spinner } from "@/components/ui";
import { useI18n } from "@/i18n";
import { PublisherIcon } from "../components/PublisherIcon";
import {
  useModelIntelBenchmarkHistory,
  useModelIntelExport,
  useModelIntelFunctions,
  useModelIntelModelCard,
  useModelIntelObservability,
  useModelIntelOwnBenchmarks,
  useModelIntelSheetHistory,
} from "../hooks/useModelIntel";
import {
  benchmarkTimeline,
  benchmarkTimelineOptions,
  cheapestOffering,
  evaluateFunctionFit,
  fieldChanges,
  ownVsPublished,
  radarPoints,
  telemetryByFunction,
  type FitReason,
} from "../lib/ficha";
import { formatDate, formatUsd } from "../lib/pricing";
import type { DomainAggregate, OfferingCard } from "../types";

function domainLabel(
  t: (key: string, params?: Record<string, string | number>) => string,
  domain: string,
): string {
  const key = `modelIntel.domains.${domain}`;
  const label = t(key);
  return label === key ? domain : label;
}

function reasonText(
  t: (key: string, params?: Record<string, string | number>) => string,
  reason: FitReason,
): string {
  const field = reason.field.startsWith("domain:")
    ? domainLabel(t, reason.field.replace("domain:", ""))
    : reason.field;
  return t(`modelIntel.card.fit.reasons.${reason.code}`, {
    field,
    value: reason.value ?? "",
    min: reason.min ?? "",
  });
}

function Section({
  title,
  testId,
  children,
}: {
  title: string;
  testId?: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="space-y-3 p-4" data-testid={testId}>
        <div className="text-sm font-medium">{title}</div>
        {children}
      </CardContent>
    </Card>
  );
}

function EmptyNote({ testId, text }: { testId?: string; text: string }) {
  return (
    <p
      className="text-sm text-gray-500 dark:text-gray-400"
      data-testid={testId}
    >
      {text}
    </p>
  );
}

function PrimitiveList({
  data,
  testId,
}: {
  data: Record<string, unknown>;
  testId?: string;
}) {
  const entries = Object.entries(data ?? {}).filter(([, value]) =>
    ["string", "number", "boolean"].includes(typeof value),
  );
  const { t } = useI18n();
  if (entries.length === 0)
    return (
      <EmptyNote
        testId={testId ? `${testId}-empty` : undefined}
        text={t("modelIntel.card.noData")}
      />
    );
  return (
    <dl
      className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm"
      data-testid={testId}
    >
      {entries.map(([key, value]) => (
        <div key={key} className="contents">
          <dt className="truncate text-gray-500 dark:text-gray-400">{key}</dt>
          <dd className="truncate font-medium">{String(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function ModelCardPage() {
  const { t } = useI18n();
  const { modelRef = "" } = useParams();
  const card = useModelIntelModelCard(modelRef);
  const functions = useModelIntelFunctions();
  const observability = useModelIntelObservability(modelRef);
  const ownBench = useModelIntelOwnBenchmarks(modelRef);
  const benchHistory = useModelIntelBenchmarkHistory(modelRef);
  const sheetHistory = useModelIntelSheetHistory(
    card.data?.offerings[0]?.offering.sheet_id ?? null,
    Boolean(card.data?.offerings[0]),
  );
  const [timelineCode, setTimelineCode] = useState("");
  const exportMutation = useModelIntelExport();

  const primary: OfferingCard | null = card.data?.offerings[0] ?? null;
  const published = useMemo(
    () => new Set((primary?.fields ?? []).map((field) => field.field)),
    [primary],
  );
  const domains: DomainAggregate[] = useMemo(
    () => card.data?.benchmarks?.domains ?? [],
    [card.data],
  );
  const points = useMemo(() => radarPoints(domains), [domains]);
  const ownComparison = useMemo(
    () => ownVsPublished(ownBench.data?.items ?? [], domains),
    [ownBench.data, domains],
  );
  const cheapest = useMemo(
    () => cheapestOffering(card.data?.offerings ?? []),
    [card.data],
  );
  const priceHistory = useMemo(() => {
    const offering = (card.data?.offerings ?? []).find(
      (item) => (item.client_price?.history?.length ?? 0) > 0,
    );
    return offering?.client_price?.history ?? [];
  }, [card.data]);
  const usageRows = useMemo(
    () => telemetryByFunction(observability.data?.items ?? []),
    [observability.data],
  );
  const timelineOptions = useMemo(
    () => benchmarkTimelineOptions(benchHistory.data?.results ?? []),
    [benchHistory.data],
  );
  const activeTimelineCode = timelineOptions.some(
    (option) => option.code === timelineCode,
  )
    ? timelineCode
    : (timelineOptions[0]?.code ?? "");
  const timeline = useMemo(
    () =>
      activeTimelineCode
        ? benchmarkTimeline(
            benchHistory.data?.results ?? [],
            activeTimelineCode,
          )
        : null,
    [benchHistory.data, activeTimelineCode],
  );
  const changes = useMemo(
    () => fieldChanges(sheetHistory.data?.history ?? []),
    [sheetHistory.data],
  );
  const fits = useMemo(
    () =>
      (functions.data?.items ?? []).map((fn) =>
        evaluateFunctionFit(fn, {
          capabilities: primary?.capabilities ?? {},
          published,
          domains,
        }),
      ),
    [functions.data, primary, published, domains],
  );

  const publisher = primary?.capabilities?.["publisher"];
  const lifecycleStatus =
    (primary?.lifecycle?.["lifecycle_status"] as string | undefined) ?? null;
  const successor = primary?.lifecycle?.["successor_model_ref"];
  const aliases = (primary?.fields ?? []).filter((field) =>
    ["aliases", "display_name", "vendor_variants"].includes(field.field),
  );

  const runExport = () => {
    exportMutation.mutate({
      kind: "models",
      format: "csv",
      include_price: card.data?.price_visibility === "included",
      model_ref: modelRef,
      limit: 200,
    });
  };

  if (card.isLoading) {
    return (
      <div
        className="flex justify-center p-10"
        data-testid="model-card-loading"
      >
        <Spinner />
      </div>
    );
  }

  if (card.isError || !card.data) {
    return (
      <div className="space-y-3 p-6" data-testid="model-card-error">
        <Link
          to="/model-intel/compare"
          className="inline-flex items-center gap-1 text-sm underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {t("modelIntel.card.back")}
        </Link>
        <div className="rounded-md border p-4 text-sm" role="alert">
          {t("modelIntel.card.error")}
        </div>
      </div>
    );
  }

  const data = card.data;

  return (
    <div className="space-y-6 p-6" data-testid="model-intel-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <PublisherIcon
            publisher={typeof publisher === "string" ? publisher : null}
            size={26}
          />
          <div>
            <h1 className="text-2xl font-semibold" data-testid="card-model-ref">
              {data.model_ref}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {lifecycleStatus ? `· ${lifecycleStatus} · ` : "· "}
              {t("modelIntel.card.generated", {
                contract: data.contract_version,
                date: formatDate(data.generated_at),
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/model-intel/compare"
            className="inline-flex items-center gap-1 text-sm underline"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            {t("modelIntel.card.back")}
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={runExport}
            disabled={exportMutation.isPending}
            data-testid="card-export"
          >
            <Download className="mr-1 h-4 w-4" aria-hidden="true" />
            {t("modelIntel.card.export")}
          </Button>
        </div>
      </div>

      {exportMutation.data && (
        <p
          className="text-xs text-gray-500 dark:text-gray-400"
          data-testid="card-export-result"
        >
          {t("modelIntel.card.exported", {
            id: exportMutation.data.export_id,
            rows: exportMutation.data.row_count,
            audited: String(exportMutation.data.audited),
          })}
        </p>
      )}

      {data.price_visibility === "omitted" && (
        <p
          className="text-xs text-amber-600 dark:text-amber-400"
          data-testid="card-price-omitted"
        >
          {t("modelIntel.card.priceOmitted")}
        </p>
      )}

      <Section
        title={t("modelIntel.card.identityTitle")}
        testId="card-identity"
      >
        <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
          <div className="contents">
            <dt className="text-gray-500 dark:text-gray-400">
              {t("modelIntel.card.implementations")}
            </dt>
            <dd>
              <ul className="space-y-0.5">
                {data.offerings.map((offering) => (
                  <li key={offering.offering.sheet_id}>
                    {offering.offering.provider_model_id}
                    <span className="ml-2 text-xs capitalize text-gray-500">
                      {offering.offering.provider} · {offering.offering.region}
                    </span>
                  </li>
                ))}
              </ul>
            </dd>
          </div>
          <div className="contents">
            <dt className="text-gray-500 dark:text-gray-400">
              {t("modelIntel.card.aliases")}
            </dt>
            <dd>
              {aliases.length === 0 ? (
                <span className="text-gray-500">
                  {t("modelIntel.card.noData")}
                </span>
              ) : (
                <ul className="space-y-0.5" data-testid="card-aliases">
                  {aliases.map((field) => (
                    <li key={field.field}>
                      {String(field.value)}
                      <span className="ml-2 text-xs text-gray-500">
                        {field.source} · {formatDate(field.observed_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        </dl>
      </Section>

      {data.profile && (
        <Section title={t("modelIntel.card.profileTitle")} testId="card-profile">
          {data.profile.description ? (
            <p className="text-sm text-gray-700 dark:text-gray-300">
              {data.profile.description}
            </p>
          ) : (
            <EmptyNote text={t("modelIntel.card.noData")} />
          )}
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
            {data.profile.primary_use && (
              <div className="contents">
                <dt className="text-gray-500 dark:text-gray-400">
                  {t("modelIntel.card.primaryUse")}
                </dt>
                <dd>{data.profile.primary_use}</dd>
              </div>
            )}
            {data.profile.family && (
              <div className="contents">
                <dt className="text-gray-500 dark:text-gray-400">
                  {t("modelIntel.card.family")}
                </dt>
                <dd>{data.profile.family}</dd>
              </div>
            )}
            {data.profile.modality && (
              <div className="contents">
                <dt className="text-gray-500 dark:text-gray-400">
                  {t("modelIntel.card.modality")}
                </dt>
                <dd>{data.profile.modality}</dd>
              </div>
            )}
            {data.profile.release_date && (
              <div className="contents">
                <dt className="text-gray-500 dark:text-gray-400">
                  {t("modelIntel.card.releaseDate")}
                </dt>
                <dd>{data.profile.release_date}</dd>
              </div>
            )}
            {data.profile.knowledge_cutoff && (
              <div className="contents">
                <dt className="text-gray-500 dark:text-gray-400">
                  {t("modelIntel.card.knowledgeCutoff")}
                </dt>
                <dd>{data.profile.knowledge_cutoff}</dd>
              </div>
            )}
            {data.profile.open_weights != null && (
              <div className="contents">
                <dt className="text-gray-500 dark:text-gray-400">
                  {t("modelIntel.card.openWeights")}
                </dt>
                <dd>{String(data.profile.open_weights)}</dd>
              </div>
            )}
            {data.profile.license && (
              <div className="contents">
                <dt className="text-gray-500 dark:text-gray-400">
                  {t("modelIntel.card.license")}
                </dt>
                <dd>{data.profile.license}</dd>
              </div>
            )}
          </dl>
          {data.global_rating && (
            <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="card-global-rating">
              {t("modelIntel.card.globalRating", {
                value: data.global_rating.value,
                source: data.global_rating.source ?? "",
              })}
            </p>
          )}
        </Section>
      )}

      <Section title={t("modelIntel.card.fitTitle")} testId="card-fit">
        {fits.length === 0 ? (
          <EmptyNote
            testId="card-fit-empty"
            text={t("modelIntel.card.fit.empty")}
          />
        ) : (
          <ul className="flex flex-wrap gap-2">
            {fits.map((fit) => (
              <li key={fit.code}>
                <Link
                  to={`/model-intel/functions/${encodeURIComponent(fit.code)}`}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${
                    fit.verdict === "apto"
                      ? "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300"
                      : fit.verdict === "no_apto"
                        ? "border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
                        : "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
                  }`}
                  title={
                    fit.reasons.length
                      ? fit.reasons
                          .map((reason) => reasonText(t, reason))
                          .join(" · ")
                      : undefined
                  }
                  data-testid={`fit-${fit.code}`}
                >
                  <span className="font-medium">{fit.name}</span>
                  <span>
                    {t(`modelIntel.card.fit.verdicts.${fit.verdict}`)}
                  </span>
                </Link>
                {fit.reasons.length > 0 && (
                  <ul
                    className="mt-1 list-disc pl-4 text-xs text-gray-500 dark:text-gray-400"
                    data-testid={`fit-reasons-${fit.code}`}
                  >
                    {fit.reasons.slice(0, 3).map((reason) => (
                      <li key={`${reason.code}-${reason.field}`}>
                        {reasonText(t, reason)}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={t("modelIntel.card.radarTitle")} testId="card-radar">
        {points.length === 0 ? (
          <EmptyNote
            testId="card-radar-empty"
            text={t("modelIntel.card.noData")}
          />
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={points} outerRadius="70%">
              <PolarGrid />
              <PolarAngleAxis
                dataKey="domain"
                tickFormatter={(value: string) => domainLabel(t, value)}
              />
              <PolarRadiusAxis domain={[0, 100]} tick={false} />
              <Radar
                dataKey="score"
                stroke="#2563eb"
                fill="#2563eb"
                fillOpacity={0.35}
              />
            </RadarChart>
          </ResponsiveContainer>
        )}
      </Section>

      <Section
        title={t("modelIntel.card.benchmarksTitle")}
        testId="card-benchmarks"
      >
        {domains.length === 0 ? (
          <EmptyNote
            testId="card-benchmarks-empty"
            text={t("modelIntel.card.noData")}
          />
        ) : (
          <div className="space-y-3">
            {domains.map((domain) => (
              <div key={domain.domain}>
                <div className="mb-1 flex items-center gap-2 text-sm font-medium">
                  <RadarIcon className="h-4 w-4" aria-hidden="true" />
                  {domainLabel(t, domain.domain)}
                  <span className="tabular-nums text-gray-500">
                    {domain.score.toFixed(1)}
                  </span>
                </div>
                <table
                  className="w-full text-xs"
                  data-testid={`domain-${domain.domain}`}
                >
                  <thead>
                    <tr className="text-left text-gray-500 dark:text-gray-400">
                      <th className="py-1 pr-2">
                        {t("modelIntel.card.colBenchmark")}
                      </th>
                      <th className="py-1 pr-2">
                        {t("modelIntel.card.colValue")}
                      </th>
                      <th className="py-1 pr-2">
                        {t("modelIntel.card.colPercentile")}
                      </th>
                      <th className="py-1 pr-2">
                        {t("modelIntel.card.colVersion")}
                      </th>
                      <th className="py-1 pr-2">
                        {t("modelIntel.card.colSource")}
                      </th>
                      <th className="py-1">{t("modelIntel.card.colDate")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(domain.components ?? []).map((component, index) => {
                      const row: Record<string, unknown> = { ...component };
                      return (
                        <tr
                          key={`${domain.domain}-${String(row["benchmark_code"] ?? index)}`}
                        >
                          <td className="py-1 pr-2">
                            {String(
                              row["benchmark_name"] ??
                                row["benchmark_code"] ??
                                "—",
                            )}
                            {row["stale"] === true && (
                              <span className="ml-2 rounded bg-amber-100 px-1 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                {t("modelIntel.card.staleBadge")}
                              </span>
                            )}
                          </td>
                          <td className="py-1 pr-2 tabular-nums">
                            {typeof row["value"] === "number"
                              ? row["value"]
                              : "—"}
                          </td>
                          <td className="py-1 pr-2 tabular-nums">
                            {typeof row["percentile"] === "number"
                              ? `${Number(row["percentile"]).toFixed(0)}`
                              : "—"}
                          </td>
                          <td className="py-1 pr-2">
                            {String(row["benchmark_version"] ?? "—")}
                          </td>
                          <td className="py-1 pr-2">
                            {String(row["source"] ?? "—")}
                          </td>
                          <td className="py-1">
                            {formatDate(
                              typeof row["published_at"] === "string"
                                ? row["published_at"]
                                : null,
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        title={t("modelIntel.card.timelineTitle")}
        testId="card-timeline"
      >
        {timelineOptions.length === 0 ? (
          <EmptyNote
            testId="card-timeline-empty"
            text={t("modelIntel.card.noData")}
          />
        ) : (
          <div className="space-y-3">
            <label className="block text-xs text-gray-500 dark:text-gray-400">
              {t("modelIntel.card.timelineBenchmark")}
              <select
                className="mt-1 w-full rounded border bg-background px-2 py-1 text-sm"
                value={activeTimelineCode}
                onChange={(event) => setTimelineCode(event.target.value)}
                data-testid="card-timeline-select"
              >
                {timelineOptions.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.name} ({option.count})
                  </option>
                ))}
              </select>
            </label>
            {!timeline || timeline.points.length < 2 ? (
              <EmptyNote
                testId="card-timeline-single"
                text={t("modelIntel.card.timelineSingle")}
              />
            ) : (
              <div data-testid="card-timeline-chart">
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart
                    data={timeline.points}
                    margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value: string) => formatDate(value)}
                    />
                    <YAxis domain={["auto", "auto"]} />
                    <Tooltip
                      labelFormatter={(label: string) => formatDate(label)}
                      formatter={(value: number) => [
                        value,
                        t("modelIntel.card.colValue"),
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke="#2563eb"
                      dot={{ r: 2 }}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            {timeline && timeline.sources.length > 0 && (
              <p
                className="text-xs text-gray-500 dark:text-gray-400"
                data-testid="card-timeline-sources"
              >
                {t("modelIntel.card.timelineProvenance", {
                  sources: timeline.sources.join(", "),
                })}
              </p>
            )}
          </div>
        )}
      </Section>

      <Section
        title={t("modelIntel.card.capabilitiesTitle")}
        testId="card-capabilities"
      >
        {(primary?.fields ?? []).length === 0 ? (
          <EmptyNote
            testId="card-capabilities-empty"
            text={t("modelIntel.card.noData")}
          />
        ) : (
          <table className="w-full text-xs" data-testid="card-fields">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-2">{t("modelIntel.card.colField")}</th>
                <th className="py-1 pr-2">{t("modelIntel.card.colValue")}</th>
                <th className="py-1 pr-2">{t("modelIntel.card.colSource")}</th>
                <th className="py-1 pr-2">
                  {t("modelIntel.card.colObserved")}
                </th>
                <th className="py-1 pr-2">
                  {t("modelIntel.card.colConfidence")}
                </th>
                <th className="py-1">{t("modelIntel.card.colChange")}</th>
              </tr>
            </thead>
            <tbody>
              {(primary?.fields ?? []).map((field) => (
                <tr key={field.field}>
                  <td className="py-1 pr-2 font-mono">{field.field}</td>
                  <td className="py-1 pr-2">
                    {typeof field.value === "object" && field.value !== null
                      ? JSON.stringify(field.value)
                      : String(field.value)}
                  </td>
                  <td className="py-1 pr-2">
                    {field.source}
                    <span className="ml-1 text-gray-500">
                      ({field.source_kind})
                    </span>
                  </td>
                  <td className="py-1 pr-2">{formatDate(field.observed_at)}</td>
                  <td className="py-1 tabular-nums">
                    {typeof field.confidence === "number"
                      ? `${(field.confidence * 100).toFixed(0)} %`
                      : "—"}
                  </td>
                  <td className="py-1 text-gray-500 dark:text-gray-400">
                    {changes[field.field]
                      ? t("modelIntel.card.changedFrom", {
                          value:
                            typeof changes[field.field].previousValue ===
                              "object" &&
                            changes[field.field].previousValue !== null
                              ? JSON.stringify(
                                  changes[field.field].previousValue,
                                )
                              : String(changes[field.field].previousValue),
                          date: formatDate(
                            changes[field.field].previousObservedAt,
                          ),
                        })
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title={t("modelIntel.card.limitsTitle")} testId="card-limits">
          <PrimitiveList
            data={primary?.operational_limits ?? {}}
            testId="card-limits-list"
          />
        </Section>

        <Section
          title={t("modelIntel.card.lifecycleTitle")}
          testId="card-lifecycle"
        >
          <PrimitiveList
            data={primary?.lifecycle ?? {}}
            testId="card-lifecycle-list"
          />
          <p
            className="text-xs text-gray-500 dark:text-gray-400"
            data-testid="card-successor"
          >
            {t("modelIntel.card.successor")}:{" "}
            {typeof successor === "string" ? (
              <Link
                to={`/model-intel/model/${encodeURIComponent(successor)}`}
                className="underline"
              >
                {successor}
              </Link>
            ) : (
              t("modelIntel.card.noData")
            )}
          </p>
        </Section>
      </div>

      <Section title={t("modelIntel.card.priceTitle")} testId="card-price">
        {data.offerings.every(
          (offering) => offering.client_price?.state !== "available",
        ) ? (
          data.offerings.length > 0 &&
          data.offerings.every(
            (offering) => offering.price_applicability === "not_applicable",
          ) ? (
            <EmptyNote
              testId="card-price-na"
              text={t("modelIntel.card.priceNotApplicable")}
            />
          ) : (
            <EmptyNote
              testId="card-price-empty"
              text={t("modelIntel.card.priceEmpty")}
            />
          )
        ) : (
          <table className="w-full text-sm" data-testid="card-price-table">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-2">
                  {t("modelIntel.card.colProvider")}
                </th>
                <th className="py-1 pr-2">{t("modelIntel.card.colRegion")}</th>
                <th className="py-1 pr-2">{t("modelIntel.card.colInput")}</th>
                <th className="py-1 pr-2">{t("modelIntel.card.colOutput")}</th>
                <th className="py-1">{t("modelIntel.card.colValidFrom")}</th>
              </tr>
            </thead>
            <tbody>
              {data.offerings.map((offering) => {
                const price = offering.client_price;
                const input =
                  price?.components?.["input"]?.usd_per_million ?? null;
                const output =
                  price?.components?.["output"]?.usd_per_million ?? null;
                const isCheapest =
                  cheapest?.sheetId === offering.offering.sheet_id;
                return (
                  <tr
                    key={offering.offering.sheet_id}
                    data-testid={`price-${offering.offering.sheet_id}`}
                  >
                    <td className="py-1 pr-2 capitalize">
                      {offering.offering.provider}
                      {isCheapest && (
                        <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                          {t("modelIntel.card.cheapest")}
                        </span>
                      )}
                    </td>
                    <td className="py-1 pr-2">{offering.offering.region}</td>
                    <td className="py-1 pr-2 tabular-nums">
                      {input !== null
                        ? formatUsd(input)
                        : t("modelIntel.card.noData")}
                    </td>
                    <td className="py-1 pr-2 tabular-nums">
                      {output !== null
                        ? formatUsd(output)
                        : t("modelIntel.card.noData")}
                    </td>
                    <td className="py-1">
                      {price?.effective_from
                        ? formatDate(price.effective_from)
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {priceHistory.length > 0 && (
          <div data-testid="card-price-history" className="mt-3">
            <div className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">
              {t("modelIntel.card.historyTitle")}
            </div>
            <ul className="space-y-1 text-xs">
              {priceHistory.map((version) => (
                <li
                  key={`${version.version}-${version.valid_from ?? ""}`}
                  className="flex flex-wrap items-center gap-2"
                >
                  <span className="font-medium">v{version.version ?? "—"}</span>
                  <span className="text-gray-500">
                    {t("modelIntel.card.validFrom", {
                      date: formatDate(version.valid_from),
                    })}
                  </span>
                  <span className="text-gray-500">
                    {t("modelIntel.components.input")}{" "}
                    {formatUsd(
                      version.components?.["input"]?.usd_per_million ?? null,
                    )}
                    {t("modelIntel.price.perMillion")}
                  </span>
                  <span className="capitalize text-gray-500">
                    {version.state}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      <Section
        title={t("modelIntel.card.telemetryTitle")}
        testId="card-telemetry"
      >
        {usageRows.length === 0 ? (
          <EmptyNote
            testId="card-telemetry-empty"
            text={t("modelIntel.card.telemetryEmpty")}
          />
        ) : (
          <table className="w-full text-sm" data-testid="card-usage-table">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                <th className="py-1 pr-2">
                  {usageRows.some((row) => row.usageMethod)
                    ? t("modelIntel.platform.colMethod")
                    : t("modelIntel.functions.colFunction")}
                </th>
                <th className="py-1 pr-2">
                  {t("modelIntel.functions.usageTasks")}
                </th>
                <th className="py-1">
                  {t("modelIntel.functions.usageLatency")}
                </th>
              </tr>
            </thead>
            <tbody>
              {usageRows.map((row) => (
                <tr key={row.functionCode}>
                  <td className="py-1 pr-2">
                    {row.usageMethod
                      ? t(`modelIntel.platform.methods.${row.usageMethod}`)
                      : row.functionCode}
                  </td>
                  <td className="py-1 pr-2 tabular-nums">
                    {row.tasks.toLocaleString()}
                  </td>
                  <td className="py-1 tabular-nums">
                    {row.latencyP95 !== null
                      ? `${row.latencyP95.toFixed(0)} ms`
                      : t("modelIntel.card.noData")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section
        title={t("modelIntel.card.ownBenchTitle")}
        testId="card-own-bench"
      >
        {ownBench.isError || (ownBench.data?.items ?? []).length === 0 ? (
          <EmptyNote
            testId="card-own-bench-empty"
            text={t("modelIntel.card.ownBenchEmpty")}
          />
        ) : (
          <>
            <ul
              className="space-y-1.5 text-sm"
              data-testid="card-own-bench-list"
            >
              {(ownBench.data?.items ?? []).map((run) => {
                const statusKey = `modelIntel.card.ownBenchStatus.${run.status}`;
                const statusText = t(statusKey);
                return (
                  <li
                    key={run.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1"
                    data-testid={`own-bench-${run.id}`}
                  >
                    <span className="font-medium">{run.benchmark_code}</span>
                    {run.status === "ok" ? (
                      <>
                        <span className="tabular-nums">
                          {run.score !== null && run.score !== undefined
                            ? `${run.score.toFixed(1)} %`
                            : t("modelIntel.card.noData")}
                        </span>
                        {run.score_ci_low !== null &&
                          run.score_ci_low !== undefined &&
                          run.score_ci_high !== null &&
                          run.score_ci_high !== undefined && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {t("modelIntel.card.ownBenchCi", {
                                low: run.score_ci_low.toFixed(0),
                                high: run.score_ci_high.toFixed(0),
                              })}
                            </span>
                          )}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {t("modelIntel.card.ownBenchSample", {
                            count: run.sample_size,
                          })}
                        </span>
                      </>
                    ) : (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                        {statusText === statusKey ? run.status : statusText}
                      </span>
                    )}
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {formatDate(run.finished_at ?? run.created_at)}
                    </span>
                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                      {run.harness_version}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("modelIntel.card.ownBenchNote")}
            </p>
          </>
        )}
      </Section>

      <Section
        title={t("modelIntel.card.ownVsPublishedTitle")}
        testId="card-own-vs-published"
      >
        {ownComparison.length === 0 ? (
          <EmptyNote
            testId="card-own-vs-published-empty"
            text={t("modelIntel.card.ownVsPublishedEmpty")}
          />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm"
                data-testid="own-vs-published-table"
              >
                <thead>
                  <tr className="border-b text-left text-xs text-gray-500 dark:text-gray-400">
                    <th className="py-1 pr-2">
                      {t("modelIntel.card.ownVsPublishedBenchmark")}
                    </th>
                    <th className="py-1 pr-2">
                      {t("modelIntel.card.ownVsPublishedOwn")}
                    </th>
                    <th className="py-1 pr-2">
                      {t("modelIntel.card.ownVsPublishedPublished")}
                    </th>
                    <th className="py-1 pr-2">
                      {t("modelIntel.card.ownVsPublishedDelta")}
                    </th>
                    <th className="py-1">
                      {t("modelIntel.card.ownVsPublishedSample")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {ownComparison.map((row) => (
                    <tr
                      key={row.code}
                      className="border-b last:border-0"
                      data-testid={`own-vs-${row.code}`}
                    >
                      <td className="py-1 pr-2">
                        <div className="font-medium">{row.name}</div>
                        <div className="font-mono text-xs text-gray-500 dark:text-gray-400">
                          {row.harnessVersion}
                        </div>
                      </td>
                      <td className="py-1 pr-2 tabular-nums">
                        {row.own.toFixed(1)} %
                        {row.ciLow !== null && row.ciHigh !== null && (
                          <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                            {t("modelIntel.card.ownBenchCi", {
                              low: row.ciLow.toFixed(0),
                              high: row.ciHigh.toFixed(0),
                            })}
                          </span>
                        )}
                      </td>
                      <td className="py-1 pr-2 tabular-nums">
                        {row.published.toFixed(1)} %
                        <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">
                          {formatDate(row.publishedAt)}
                        </span>
                      </td>
                      <td
                        className={`py-1 pr-2 tabular-nums ${
                          row.delta >= 0 ? "text-emerald-600" : "text-amber-600"
                        }`}
                      >
                        {row.delta > 0 ? "+" : ""}
                        {row.delta.toFixed(1)}
                      </td>
                      <td className="py-1 tabular-nums">{row.sample}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("modelIntel.card.ownVsPublishedNote")}
            </p>
          </>
        )}
      </Section>

      <div className="grid gap-4 lg:grid-cols-2">
        <Section title={t("modelIntel.card.newsTitle")} testId="card-news">
          {data.news?.state === "available" && data.news.items.length > 0 ? (
            <ul className="space-y-2 text-sm" data-testid="card-news-list">
              {data.news.items.map((item) => (
                <li key={`${item.source}-${item.published_at}`}>
                  <Newspaper
                    className="mr-1 inline h-4 w-4 text-gray-400"
                    aria-hidden="true"
                  />
                  {item.url ? (
                    <a
                      href={item.url}
                      className="underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      {item.title}
                    </a>
                  ) : (
                    item.title
                  )}
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {t("modelIntel.card.newsSource", {
                      source: item.source,
                      date: formatDate(item.published_at),
                    })}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyNote
              testId="card-news-empty"
              text={t("modelIntel.card.newsEmpty")}
            />
          )}
        </Section>

        <Section
          title={t("modelIntel.card.feedbackTitle")}
          testId="card-feedback"
        >
          {data.feedback?.state === "available" && data.feedback.totals ? (
            <div data-testid="card-feedback-summary">
              <PrimitiveList
                data={data.feedback.totals}
                testId="card-feedback-totals"
              />
              <p className="mt-2 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <MessageSquare className="h-4 w-4" aria-hidden="true" />
                {t("modelIntel.card.feedbackK", {
                  k: data.feedback.k_anonymity ?? "—",
                  days: data.feedback.window_days ?? "—",
                })}
              </p>
            </div>
          ) : (
            <EmptyNote
              testId="card-feedback-empty"
              text={t("modelIntel.card.feedbackEmpty")}
            />
          )}
        </Section>
      </div>
    </div>
  );
}
